import type { AgentPolicyGrantForm } from "@gram/client/models/components/agentpolicygrantform.js";
import type {
  AgentPolicySelector,
  AgentPolicySelectorDisposition,
} from "@gram/client/models/components/agentpolicyselector.js";

/** A selector value meaning "every resource of this kind". */
export const ANY_RESOURCE = "*";

/**
 * The dimensions API keys may add on top of a policy grant. This is a subset
 * of allowedSelectorKeys in server/internal/authz/selector.go: keys never add
 * project narrowing, but must preserve any project restriction already present.
 */
const NARROWABLE_BY_KIND: Record<string, NarrowingDimension[]> = {
  mcp: ["disposition", "tool"],
};

export type NarrowingDimension = "disposition" | "tool";

/** Which inventory names the resources a selector of this kind identifies. */
export type ResourceInventory = "mcp" | "project";

export interface GrantNarrowing {
  resourceId?: string;
  /** Legacy editor state: ignored; candidate project restrictions are preserved. */
  projectId?: string;
  dispositions?: AgentPolicySelectorDisposition[];
  disposition?: AgentPolicySelectorDisposition;
  /** Legacy single-tool selections remain valid. */
  tool?: string;
  /** Undefined means any allowed tool; an empty list must not broaden access. */
  tools?: string[];
}

export interface GrantSelection {
  grant: AgentPolicyGrantForm;
  narrowing: GrantNarrowing;
}

export const DISPOSITION_LABELS: Record<
  AgentPolicySelectorDisposition,
  string
> = {
  read_only: "Read-only tools",
  destructive: "Destructive tools",
  idempotent: "Idempotent tools",
  open_world: "Open-world tools",
};

export function resourceInventoryFor(
  resourceKind: string,
): ResourceInventory | null {
  switch (resourceKind) {
    case "mcp":
      return "mcp";
    // Project and skill grants are both identified by a project id.
    case "project":
    case "skill":
      return "project";
    default:
      return null;
  }
}

/**
 * The dimensions this grant leaves open — absent, or present as a wildcard. A
 * dimension pinned to a concrete value stays fixed: replacing it would request
 * authority the candidate never carried, and the server would reject it.
 */
export function openDimensions(
  grant: AgentPolicyGrantForm,
): NarrowingDimension[] {
  const available = NARROWABLE_BY_KIND[grant.selector.resourceKind] ?? [];
  return available.filter((key) => {
    const value = grant.selector[key];
    // A wildcard constrains nothing, so replacing it narrows the request the
    // same way naming an absent dimension does.
    return value === undefined || value === ANY_RESOURCE;
  });
}

/** Whether the caller may pick one resource in place of the policy wildcard. */
export function canNarrowResource(grant: AgentPolicyGrantForm): boolean {
  return (
    grant.selector.resourceId === ANY_RESOURCE &&
    resourceInventoryFor(grant.selector.resourceKind) !== null
  );
}

/**
 * Mirrors Selector.StrictMatches in server/internal/authz/selector.go: for the
 * live policy grant to cover a request, every dimension the policy constrains
 * must appear in the request with the same value, unless the policy wildcards
 * it. Anything else is a broader request than the policy allows.
 */
export function requestNarrowsPolicy(
  policy: AgentPolicySelector,
  requested: AgentPolicySelector,
): boolean {
  const asked = requested as Record<string, string | undefined>;
  return Object.entries(policy as Record<string, string | undefined>).every(
    ([key, value]) => {
      if (value === undefined) return true;
      const chosen = asked[key];
      if (chosen === undefined) return false;
      return value === ANY_RESOURCE || value === chosen;
    },
  );
}

function expandRequestedGrants(
  selection: GrantSelection,
): AgentPolicyGrantForm[] {
  const { grant, narrowing } = selection;
  if (grant.effect !== "allow") {
    throw new Error("Only allowed permissions can be delegated.");
  }
  const selector: AgentPolicySelector = { ...grant.selector };
  if (narrowing.resourceId && canNarrowResource(grant)) {
    selector.resourceId = narrowing.resourceId;
  }
  const open = new Set(openDimensions(grant));
  // Each axis is a union; combining axes intersects them. Always clone the
  // complete candidate so project, tool, server identity and other ceilings
  // survive expansion. Legacy scalar state remains supported.
  const tools = open.has("tool")
    ? (narrowing.tools ?? (narrowing.tool ? [narrowing.tool] : undefined))
    : undefined;
  const dispositions = open.has("disposition")
    ? (narrowing.dispositions ??
      (narrowing.disposition ? [narrowing.disposition] : undefined))
    : undefined;
  // An explicit empty selection must not silently become unrestricted.
  if (
    tools?.length === 0 ||
    dispositions?.length === 0 ||
    tools?.some((tool) => !tool || tool === ANY_RESOURCE)
  ) {
    throw new Error(
      "Select at least one tool or disposition, or reset the permission.",
    );
  }
  return [...new Set(tools ?? [undefined])].flatMap((tool) =>
    [...new Set(dispositions ?? [undefined])].map((disposition) => ({
      effect: grant.effect,
      scope: grant.scope,
      selector: {
        ...selector,
        ...(tool ? { tool } : {}),
        ...(disposition ? { disposition } : {}),
      },
    })),
  );
}

/**
 * Stable identity of a grant. Delegable candidates carry no id, so this keys
 * both the editor's per-candidate state and the duplicate check the server
 * would otherwise fail on.
 */
export function delegableGrantKey(form: AgentPolicyGrantForm): string {
  const selector = form.selector as Record<string, string | undefined>;
  // Structured and escaped rather than joined with delimiters. Selector values
  // are arbitrary strings, so under a sorted `key=value` join two adjacent
  // free-form dimensions collide: a `serverIdentity` ending in
  // `&serverUrl=…` keyed identically to a grant constraining both. Every caller
  // treats a shared key as one grant — the duplicate check, the diff, and the
  // per-candidate editor state.
  return JSON.stringify([
    form.effect,
    form.scope,
    Object.keys(selector)
      .filter((key) => selector[key] !== undefined)
      .sort()
      .map((key) => [key, selector[key]]),
  ]);
}

/**
 * The exact grants to request for the selected policy grants. Throws rather
 * than sending anything the agent policy does not already cover: the server is
 * the authority, and a request it would reject is an editor bug, not a prompt.
 */
export function buildRequestedGrants(
  selections: GrantSelection[],
): AgentPolicyGrantForm[] {
  const forms: AgentPolicyGrantForm[] = [];
  const seen = new Set<string>();
  for (const selection of selections) {
    for (const form of expandRequestedGrants(selection)) {
      if (!requestNarrowsPolicy(selection.grant.selector, form.selector)) {
        throw new Error(
          "A requested permission is broader than the agent policy allows. Reset it and try again.",
        );
      }
      const identity = delegableGrantKey(form);
      if (seen.has(identity)) {
        throw new Error(
          "Two requested permissions are identical. Narrow or remove one of them.",
        );
      }
      seen.add(identity);
      forms.push(form);
    }
  }
  return forms;
}

// Match Go strings.TrimSpace and utf8.RuneCountInString used by agent issuance.
export function validateAgentAPIKeyName(value: string): string {
  const name = value.replace(/^\p{White_Space}+|\p{White_Space}+$/gu, "");
  if (!name) throw new Error("Enter a key name.");
  for (const prefix of ["plugins-", "litellm-"]) {
    if (name.startsWith(prefix))
      throw new Error(
        `Key names starting with "${prefix}" are reserved. Choose another name.`,
      );
  }
  if (Array.from(name).length > 255)
    throw new Error(
      "Key names must not exceed 255 Unicode characters. Shorten the name.",
    );
  return name;
}
